/**
 * Boneca do Provador Virtual — Versiani Swim.
 *
 * Manequim montado a partir dos RENDERS da VS (`Base/Chest/Waits/Hips.png`,
 * entregues em 2026-08-16). Cada folha traz 5 variações do mesmo corpo mudando
 * uma região; aqui elas viram um corpo só, respondendo aos três sliders.
 *
 * Por que montar em vez de escolher uma imagem pronta: são 5×5×5 = 125
 * combinações. A Sizebay resolve isso pré-renderizando as 125 (é o que o nome
 * `030303.jpg` do arquivo deles diz). Com três camadas e uma transição vertical
 * chega-se no mesmo lugar com 15 arquivos e sem render novo a cada ajuste de
 * modelagem.
 *
 * ═══ O QUE FOI FEITO NAS IMAGENS ═══
 *
 *  · recorte numa grade fixa e ALINHAMENTO PELA CABEÇA — as variações não
 *    estavam na mesma coluna da folha (entre o quadril 1 e o 5 o corpo anda
 *    ~50 px), e empilhar sem alinhar dava degrau no corpo montado;
 *  · limpeza do alpha (abertura morfológica + maior componente): o render veio
 *    com bloom grudado no contorno;
 *  · cinza + alfa em vez de RGBA, porque o manequim é acromático.
 *
 * O alinhamento saiu bom: topo da cabeça em 7–8 e base dos pés em 712–715 nos
 * quinze arquivos. O que NÃO ficou igual entre folhas — e é o que as constantes
 * abaixo corrigem — é o braço esquerdo e o tom da folha da cintura.
 *
 * ═══ CONFERIR ═══
 *
 *   scripts/prova/medir-bonecas.html   mede os PNGs (silhueta, tom, braços)
 *   scripts/prova/prova-bonecas.html   monta os corpos extremos lado a lado
 *
 * Rodam em `file://` com `--allow-file-access-from-files`. É preciso medir aqui
 * e não no navegador do cliente: em produção as imagens vêm do CDN e contaminam
 * o canvas, então `getImageData` deixa de ser possível.
 *
 * API:
 *   VSBoneca.desenhar(canvas, corpo, { destaques: ['busto'], recorte: 'torso' })
 *   VSBoneca.indice(medida, eixo) → 1..5
 *   VSBoneca.proporcao(recorte) → largura/altura do que vai ser pintado
 */
(function () {
  'use strict';

  var LARG = 408, ALT = 724;                    // quadro dos recortes

  /** Onde a fita passa, em fração da altura do quadro. */
  var ALTURAS = { busto: 0.283, cintura: 0.392, quadril: 0.458 };

  /**
   * Extensão horizontal [esquerda, direita] do TRONCO nas três alturas, por
   * parte e variação. Medida no próprio PNG (`scripts/prova/medir-bonecas.html`) varrendo do
   * eixo do corpo para fora até o primeiro pixel vazio — a varredura para
   * sozinha na axila, que é o que separa tronco de braço.
   *
   * A tabela anterior vinha da silhueta INTEIRA (`corridas()[0][0]` até a
   * última), ou seja de braço a braço: a fita saía com ~40% da largura do quadro
   * e passava por fora do corpo. Confere pelo corpo de referência 91-75-97 —
   * busto/cintura medidos dão 126/107 = 1,18 contra 91/75 = 1,21 em cm.
   */
  var SILHUETA = {
    busto: [
      { b: [148, 273], c: [153, 261], q: [132, 271] },
      { b: [151, 277], c: [155, 265], q: [135, 275] },
      { b: [149, 275], c: [154, 264], q: [133, 274] },
      { b: [147, 276], c: [154, 265], q: [133, 275] },
      { b: [146, 278], c: [151, 267], q: [130, 277] }
    ],
    cintura: [
      { b: [153, 275], c: [161, 257], q: [145, 266] },
      { b: [152, 276], c: [159, 259], q: [142, 270] },
      { b: [151, 277], c: [157, 264], q: [138, 275] },
      { b: [150, 278], c: [156, 267], q: [137, 279] },
      { b: [148, 279], c: [151, 270], q: [129, 284] }
    ],
    quadril: [
      { b: [148, 279], c: [157, 261], q: [135, 271] },
      { b: [147, 279], c: [156, 263], q: [134, 273] },
      { b: [146, 279], c: [153, 265], q: [133, 274] },
      { b: [144, 280], c: [149, 267], q: [129, 276] },
      { b: [144, 280], c: [144, 268], q: [125, 276] }
    ]
  };

  /**
   * Variações 0 e 6: geradas por DEFORMAÇÃO dos renders 1 e 5
   * (`scripts/prova/extrapolar-bonecas.py`), para o slider do ajuste de
   * formato ter imagem nos cinco passos quando o corpo estimado cai na
   * variação 2 ou 4. Extensões medidas nos PNGs gerados, mesmo método.
   */
  var SILHUETA_EXTRA = {
    busto: { 0: { b: [150, 272], c: [154, 261], q: [132, 271] }, 6: { b: [146, 279], c: [152, 267], q: [131, 276] } },
    cintura: { 0: { b: [153, 275], c: [164, 253], q: [145, 266] }, 6: { b: [149, 279], c: [149, 272], q: [129, 284] } },
    quadril: { 0: { b: [149, 279], c: [158, 260], q: [138, 268] }, 6: { b: [144, 279], c: [144, 268], q: [123, 278] } }
  };

  function silhueta(parte, i) {
    return (SILHUETA_EXTRA[parte] || {})[i] || SILHUETA[parte][i - 1];
  }

  /**
   * Medida (cm) → variação 1..5.
   *
   * Ancorado no corpo de referência 91-75-97, que é o que o provador em
   * produção devolve para 165 cm / 60 kg / 30 anos: esse corpo cai na variação
   * 3, o meio da escala. O passo é a diferença de circunferência que justifica
   * mudar de manequim — não é medida exata, é escolha de ilustração.
   */
  var ESCALA = {
    busto: { centro: 91, passo: 7 },
    cintura: { centro: 75, passo: 8 },
    quadril: { centro: 97, passo: 8 }
  };

  function indice(medida, eixo) {
    var e = ESCALA[eixo];
    if (!e || !medida) return 3;
    return Math.max(1, Math.min(5, Math.round(3 + (medida - e.centro) / e.passo)));
  }

  /* ------------------------------------------------------------- imagens */

  var cache = {};

  function url(parte, i) {
    var mapa = window.VSBonecaUrls || {};
    // A lista vem do Liquid indexada de 0 a 6 (0 e 6 são as variações
    // geradas por scripts/prova/extrapolar-bonecas.py).
    return (mapa[parte] || [])[i] || '';
  }

  function carregar(parte, i) {
    var chave = parte + i;
    if (cache[chave]) return cache[chave];
    cache[chave] = new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('não carregou ' + chave)); };
      img.src = url(parte, i);
    });
    return cache[chave];
  }

  /* ------------------------------------------------------------- desenho */

  /** Faixa do quadro que cada recorte usa, em fração da altura. */
  var FAIXAS = { torso: [0.10, 0.56], cheio: [0, 1] };

  /**
   * Onde a faixa recortada cai dentro do canvas. Só conta — não desenha.
   * Antes isto era o retorno de `desenharQuadro` e a fita precisava de um canvas
   * descartável só para recuperar os números.
   */
  function metricas(W, H, recorte) {
    var faixa = FAIXAS[recorte] || FAIXAS.cheio;
    var sy = faixa[0] * ALT;
    var sh = (faixa[1] - faixa[0]) * ALT;
    // mantém a proporção: encaixa a faixa dentro do canvas, centralizada
    var escala = Math.min(W / LARG, H / sh);
    return {
      sy: sy, sh: sh, escala: escala,
      dw: LARG * escala, dh: sh * escala,
      dx: (W - LARG * escala) / 2, dy: (H - sh * escala) / 2
    };
  }

  /** Canvases de rascunho, criados uma vez: com o slider em movimento isto
   *  repinta a cada quadro, e alocar canvas por repintura entope o GC à toa. */
  var pratos = {};

  function prato(nome, W, H) {
    var p = pratos[nome];
    if (!p) p = pratos[nome] = document.createElement('canvas');
    if (p.width !== W || p.height !== H) { p.width = W; p.height = H; }
    var c = p.getContext('2d');
    c.globalCompositeOperation = 'source-over';
    c.clearRect(0, 0, W, H);
    return c;
  }

  /**
   * Onde as folhas se trocam, em fração da altura do QUADRO — não do canvas.
   *
   * Ancorado nos marcos de ALTURAS: cada folha tem que estar 100% opaca na
   * altura que ela manda (busto .283, cintura .392, quadril .458) e ter sumido
   * antes do marco seguinte. Por isso cada faixa cai no meio do caminho entre
   * dois marcos. Escrever isto em fração do CANVAS, como estava, tem dois
   * problemas: a faixa anda quando o canvas muda de proporção (o quadro é
   * encaixado com letterbox) e some de vez no recorte 'torso'.
   */
  var TROCA = {
    busto: [0.295, 0.380],     // meio do caminho busto → cintura (.3375)
    cintura: [0.398, 0.452]    // meio do caminho cintura → quadril (.425)
  };

  /**
   * A folha da CINTURA saiu do render ~3,5/255 mais clara que as outras duas.
   * Medindo o mesmo pedaço de ventre nas variações do meio (mesmo corpo):
   * busto-3 = 213,2 · quadril-3 = 213,8 · cintura-3 = 217,2. Busto e quadril
   * batem; a cintura destoa.
   *
   * São 1,6% — invisível numa imagem isolada, e uma LINHA HORIZONTAL bem
   * visível atravessando o quadril quando duas folhas se cruzam num degradê
   * curto (banda de Mach). Corrigir o tom é mais honesto que alargar a
   * transição, porque alargar empurra a folha errada para cima do marco de
   * medida. Onde `ctx.filter` não existe (Safari < 17) volta a ser a banda
   * discreta de antes — nada quebra.
   */
  var BRILHO_CINTURA = 213.5 / 217.2;

  /**
   * ═══ O BRAÇO ESQUERDO ═══
   *
   * As três folhas NÃO são o mesmo render. Medindo os PNGs (`scripts/prova/medir-bonecas.html`)
   * o braço direito bate entre elas (266/266/264, ±2 px) mas o esquerdo não: a
   * folha do QUADRIL tem o braço ~10 px mais à esquerda que as de busto e
   * cintura, em todas as cinco variações. Não é recorte torto — o eixo do corpo
   * e o braço direito coincidem; é a pose do braço no render.
   *
   * Numa mistura simples isso aparece como BRAÇO FANTASMA: na faixa de troca as
   * duas folhas ficam meio-transparentes e vê-se dois braços. E o `source-over`
   * não resolve, porque onde a folha de cima é fundo transparente a de baixo
   * continua aparecendo — o braço deslocado atravessa a camada.
   *
   * Solução: à ESQUERDA de `CORREDOR` a folha da cintura fica opaca até os
   * braços acabarem, e a composição SUBSTITUI em vez de sobrepor (apaga o
   * destino pela máscara antes de desenhar). Assim o braço vem sempre da mesma
   * folha. O corredor cabe: entre a borda interna do braço e a borda do tronco
   * sobram 23–37 px em toda a faixa que interessa.
   *
   * Só a camada da cintura precisa disso. Entre busto e cintura o braço difere
   * 2 px — invisível.
   */
  var CORREDOR = [116, 121];        // x no quadro: fim do "fora", início do "dentro"
  var FIM_BRACOS = [0.55, 0.60];    // altura em que os braços acabam

  /**
   * ═══ COSTURA ENTRE FOLHAS ═══
   *
   * Nas faixas de TROCA a folha de cima some num degradê sobre a de baixo. Se
   * as duas têm larguras de tronco diferentes ali (cintura −2 com quadril +2,
   * por exemplo), aparecem DUAS bordas meio transparentes: um degrau/entalhe no
   * contorno. Não é defeito das variações geradas — acontece com os renders
   * originais nas combinações opostas; as variações 0/6 só tornam mais
   * combinações alcançáveis.
   *
   * Solução: antes de misturar, cada linha da faixa é deformada na horizontal
   * para as duas folhas terem a MESMA borda de tronco — no topo da faixa a
   * borda da folha de cima, no pé a da de baixo, suave no meio. Só o tronco e o
   * vão até o braço andam (margem `VAO`); o braço fica onde está. As alturas da
   * fita (.283/.392/.458) ficam fora das faixas, então a medida não muda.
   */
  var EIXO_X = 211;
  var VAO = 24;                   // px do quadro além da borda que acompanham
  // Maior diferença de borda entre folhas que ainda se costura. Era 12: o quadril 6 contra a
  // cintura 3 difere 12–16 px na borda esquerda e ficava sem costura (degrau no quadril,
  // Nicolas 2026-10-15). Braço colado continua barrado pelo vão (me/md < 3).
  var SALTO = 24;
  var costuras = {};              // cache por combinação de variações
  var costurasOrdem = [];

  function dadosDe(img) {
    var c = document.createElement('canvas');
    c.width = LARG; c.height = ALT;
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0, LARG, ALT);
    return { canvas: c, ctx: x, dados: x.getImageData(0, 0, LARG, ALT) };
  }

  /**
   * [esquerda, direita, vãoEsq, vãoDir] do tronco na linha y: do eixo até o
   * 1º pixel vazio, e quanto de fundo há até o braço de cada lado. O vão manda
   * na margem da deformação — perto do cotovelo o braço encosta no tronco e
   * uma margem fixa arrastava pixels do braço (um retângulo aparecia ali).
   */
  function bordas(d, y) {
    var a = d.data, base = y * LARG * 4;
    if (a[base + EIXO_X * 4 + 3] < 128) return null;
    var e = EIXO_X, r = EIXO_X;
    while (e > 0 && a[base + (e - 1) * 4 + 3] >= 128) e--;
    while (r < LARG - 1 && a[base + (r + 1) * 4 + 3] >= 128) r++;
    var ge = 0, gd = 0;
    while (ge < VAO + 4 && e - ge - 1 > 0 && a[base + (e - ge - 1) * 4 + 3] < 128) ge++;
    while (gd < VAO + 4 && r + gd + 1 < LARG - 1 && a[base + (r + gd + 1) * 4 + 3] < 128) gd++;
    return [e, r, ge, gd];
  }

  /** Remapeia uma linha: [x0, e, r, x1] → [x0, e2, r2, x1]; x0/x1 param no braço. */
  function remapearLinha(orig, dest, y, e, r, e2, r2, me, md) {
    var a = orig.data, b = dest.data, base = y * LARG * 4;
    var x0 = Math.max(0, e - me), x1 = Math.min(LARG - 1, r + md);
    for (var x = x0; x <= x1; x++) {
      var s;
      if (x < e2) s = x0 + (x - x0) * (e - x0) / Math.max(1e-3, e2 - x0);
      else if (x <= r2) s = e + (x - e2) * (r - e) / Math.max(1e-3, r2 - e2);
      else s = r + (x - r2) * (x1 - r) / Math.max(1e-3, x1 - r2);
      var i0 = Math.max(0, Math.min(LARG - 1, Math.floor(s))), i1 = Math.min(LARG - 1, i0 + 1), f = s - i0;
      for (var k = 0; k < 4; k++) {
        b[base + x * 4 + k] = a[base + i0 * 4 + k] * (1 - f) + a[base + i1 * 4 + k] * f;
      }
    }
  }

  /**
   * Deforma `alvo` nas linhas da faixa [y0, y1] para que a borda do tronco vá
   * da borda de `cima` (em y0) para a de `baixo` (em y1).
   */
  function costurar(alvo, cima, baixo, y0, y1) {
    for (var y = Math.floor(y0); y <= Math.ceil(y1); y++) {
      var bA = bordas(alvo.orig, y), bC = bordas(cima.orig, y), bB = bordas(baixo.orig, y);
      if (!bA || !bC || !bB) continue;
      var t = Math.max(0, Math.min(1, (y - y0) / (y1 - y0)));
      t = t * t * (3 - 2 * t);
      // margem de cada lado = o vão até o braço, menos 2 px de folga; a borda
      // só anda até 3 px antes dessa margem (nunca encosta no braço)
      var me = Math.max(0, Math.min(VAO, bA[2] - 2)), md = Math.max(0, Math.min(VAO, bA[3] - 2));
      var e2 = bC[0] + (bB[0] - bC[0]) * t, r2 = bC[1] + (bB[1] - bC[1]) * t;
      // Lado sem vão (braço encostado) ou borda que salta mais de 12 px entre
      // as folhas (a varredura atravessou um braço colado): esse lado não anda.
      if (me < 3 || Math.abs(bC[0] - bB[0]) > SALTO || Math.abs(bA[0] - bC[0]) > SALTO || Math.abs(bA[0] - bB[0]) > SALTO) e2 = bA[0];
      if (md < 3 || Math.abs(bC[1] - bB[1]) > SALTO || Math.abs(bA[1] - bC[1]) > SALTO || Math.abs(bA[1] - bB[1]) > SALTO) r2 = bA[1];
      e2 = Math.max(bA[0] - Math.max(0, me - 3), Math.min(bA[0] + SALTO, e2));
      r2 = Math.max(bA[1] - SALTO, Math.min(bA[1] + Math.max(0, md - 3), r2));
      if (Math.abs(e2 - bA[0]) < 0.3 && Math.abs(r2 - bA[1]) < 0.3) continue;
      remapearLinha(alvo.orig, alvo.dados, y, bA[0], bA[1], e2, r2, me, md);
    }
  }

  /**
   * ═══ BRAÇO ESQUERDO, LINHA A LINHA ═══
   *
   * O corredor fixo (`CORREDOR`, x 116–121) cortava o QUADRIL: com o quadril
   * largo (variações 5–6) a borda esquerda do quadril passa de x 116, e tudo à
   * esquerda dali vinha da folha da cintura, que é estreita. Resultado: o lado
   * esquerdo do quadril/coxa com um contorno fantasma mais fino que o direito
   * (Nicolas, 2026-10-15). O que tem de vir da cintura é só o BRAÇO.
   *
   * Por linha da faixa do corredor: a borda interna do braço esquerdo da folha
   * da cintura = fim do 1º trecho opaco a partir da esquerda, desde que haja vão
   * depois dele (senão o braço encosta no tronco e vale o corredor fixo).
   */
  /**
   * Fator de brilho que iguala a folha da cintura à do quadril NA faixa de troca,
   * medido nesta combinação. O fixo `BRILHO_CINTURA` foi medido nas variações do
   * meio (3 com 3); com o quadril nas pontas (5–6) a diferença muda e a banda
   * horizontal na barriga voltava (Nicolas, 2026-10-15). Média do ventre perto
   * do eixo, onde as duas folhas têm pele nas duas — fora disso, o fixo.
   */
  function tomEntre(cint, quad) {
    var y0 = Math.floor(TROCA.cintura[0] * ALT), y1 = Math.ceil(TROCA.cintura[1] * ALT);
    var sc = 0, sq = 0, n = 0;
    for (var y = y0; y <= y1; y++) {
      for (var x = EIXO_X - 30; x <= EIXO_X + 30; x++) {
        var i = (y * LARG + x) * 4;
        if (cint[i + 3] < 250 || quad[i + 3] < 250) continue;
        sc += cint[i] + cint[i + 1] + cint[i + 2];
        sq += quad[i] + quad[i + 1] + quad[i + 2];
        n++;
      }
    }
    if (n < 200 || !sc) return BRILHO_CINTURA;
    var f = sq / sc;
    return f > 0.9 && f < 1.1 ? f : BRILHO_CINTURA;
  }

  function bracoEsquerdo(d) {
    var a = d.data, y0 = Math.floor(TROCA.cintura[0] * ALT), y1 = Math.ceil(FIM_BRACOS[1] * ALT);
    var borda = {};
    for (var y = y0; y <= y1; y++) {
      var base = y * LARG * 4, x = 0;
      while (x < EIXO_X && a[base + x * 4 + 3] < 128) x++;          // fundo até o braço
      if (x >= EIXO_X) continue;
      while (x < EIXO_X && a[base + x * 4 + 3] >= 128) x++;         // o braço
      var fim = x;
      while (x < EIXO_X && a[base + x * 4 + 3] < 128) x++;          // vão até o tronco
      if (x - fim >= 2 && fim < EIXO_X - 20) borda[y] = fim;
    }
    return borda;
  }

  /**
   * As três folhas já costuradas para esta combinação, como canvases no
   * tamanho do quadro. Sem acesso aos pixels (canvas contaminado), devolve as
   * imagens originais — a costura some, o desenho não quebra.
   */
  function folhasCosturadas(imgs, chave) {
    if (costuras[chave]) return costuras[chave];
    var out;
    try {
      var f = imgs.map(function (img) {
        var d = dadosDe(img);
        return { canvas: d.canvas, ctx: d.ctx, orig: d.dados, dados: d.ctx.getImageData(0, 0, LARG, ALT) };
      });
      var zB = [TROCA.busto[0] * ALT, TROCA.busto[1] * ALT];
      var zC = [TROCA.cintura[0] * ALT, TROCA.cintura[1] * ALT];
      costurar(f[0], f[0], f[1], zB[0], zB[1]);   // busto → cintura
      costurar(f[1], f[0], f[1], zB[0], zB[1]);
      costurar(f[1], f[1], f[2], zC[0], zC[1]);   // cintura → quadril
      costurar(f[2], f[1], f[2], zC[0], zC[1]);
      out = f.map(function (x) { x.ctx.putImageData(x.dados, 0, 0); return x.canvas; });
      out.braco = bracoEsquerdo(f[1].dados);
      out.brilho = tomEntre(f[1].dados, f[2].dados);
    } catch (e) {
      out = imgs;
    }
    costuras[chave] = out;
    costurasOrdem.push(chave);
    if (costurasOrdem.length > 16) delete costuras[costurasOrdem.shift()];
    return out;
  }

  /** Fração da altura do quadro → y no canvas, normalizado em 0..1. */
  function paraCanvas(ff, q, H) {
    return Math.max(0, Math.min(1, (q.dy + (ff * ALT - q.sy) * q.escala) / H));
  }

  /** Gradiente vertical: opaco até `ate`, transparente a partir de `some`. */
  function degrade(c, H, ate, some, q) {
    var g = c.createLinearGradient(0, 0, 0, H);
    ate = paraCanvas(ate, q, H);
    some = paraCanvas(some, q, H);
    g.addColorStop(0, 'rgba(0,0,0,1)');
    g.addColorStop(Math.max(0, Math.min(1, ate)), 'rgba(0,0,0,1)');
    g.addColorStop(Math.max(0, Math.min(1, Math.max(some, ate + 0.001))), 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    return g;
  }

  /**
   * Máscara alfa da camada, em canvas. `corredor` liga o tratamento do braço.
   */
  function mascara(W, H, q, troca, corredor, braco) {
    var c = prato('mascara', W, H);
    c.fillStyle = degrade(c, H, troca[0], troca[1], q);
    c.fillRect(0, 0, W, H);
    if (!corredor) return pratos.mascara;

    // Fora do corredor a folha continua opaca até o fim dos braços.
    var t = prato('corredor', W, H);
    t.fillStyle = degrade(t, H, FIM_BRACOS[0], FIM_BRACOS[1], q);
    t.fillRect(0, 0, W, H);

    t.globalCompositeOperation = 'destination-in';
    if (braco && Object.keys(braco).length) {
      // Só o braço (até 2 px depois da borda interna dele) fica na folha da
      // cintura; linhas sem braço medido usam a última borda conhecida.
      var y0 = Math.floor(TROCA.cintura[0] * ALT), y1 = Math.ceil(FIM_BRACOS[1] * ALT), ult = CORREDOR[0];
      var s = prato('braco', W, H);
      s.fillStyle = '#000';
      // acima da faixa medida vale o corredor fixo (o braço ainda está longe do tronco)
      s.fillRect(0, 0, q.dx + CORREDOR[0] * q.escala, Math.max(0, q.dy + (y0 - q.sy) * q.escala));
      for (var y = y0; y <= y1; y++) {
        if (braco[y] != null) ult = Math.min(braco[y] + 2, CORREDOR[1]);
        var cy = q.dy + (y - q.sy) * q.escala;
        s.fillRect(0, Math.floor(cy), q.dx + ult * q.escala, Math.ceil(q.escala) + 1);
      }
      t.drawImage(pratos.braco, 0, 0);
    } else {
      var x0 = q.dx + CORREDOR[0] * q.escala;
      var x1 = q.dx + CORREDOR[1] * q.escala;
      var h = t.createLinearGradient(x0, 0, x1, 0);
      h.addColorStop(0, 'rgba(0,0,0,1)');
      h.addColorStop(1, 'rgba(0,0,0,0)');
      t.fillStyle = h;
      t.fillRect(0, 0, W, H);
    }

    // União suave das duas: dentro do corredor sobra só a troca vertical.
    c.drawImage(pratos.corredor, 0, 0);
    return pratos.mascara;
  }

  /**
   * Camada mascarada, desenhada por SUBSTITUIÇÃO: apaga o destino na máscara e
   * põe a folha no lugar. Sobrepor não serve — ver o comentário do braço.
   *
   * Sem máscara (`troca == null`) vai direto no destino: a base não precisa de
   * intermediário.
   */
  function camada(ctx, img, W, H, q, o) {
    if (!o.troca) {
      ctx.drawImage(img, 0, q.sy, LARG, q.sh, q.dx, q.dy, q.dw, q.dh);
      return;
    }

    var m = mascara(W, H, q, o.troca, o.corredor, o.braco);

    var c = prato('camada', W, H);
    if (o.brilho) c.filter = 'brightness(' + o.brilho + ')';
    c.drawImage(img, 0, q.sy, LARG, q.sh, q.dx, q.dy, q.dw, q.dh);
    c.filter = 'none';
    c.globalCompositeOperation = 'destination-in';
    c.drawImage(m, 0, 0);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(m, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(pratos.camada, 0, 0);
  }

  /** Fita de medida: elipse abraçando o corpo, com a frente marcada. */
  function fita(ctx, quadro, extensao, yQuadro) {
    var esq = quadro.dx + extensao[0] * quadro.escala;
    var dir = quadro.dx + extensao[1] * quadro.escala;
    var cx = (esq + dir) / 2;
    var rx = (dir - esq) / 2 + 2;
    var cy = quadro.dy + (yQuadro - quadro.sy) * quadro.escala;
    var ry = Math.max(4, rx * 0.2);

    // Suave: os dois arcos com a MESMA espessura e a frente se fundindo na
    // volta de trás pelas pontas — antes havia um degrau de espessura e de cor
    // nas laterais. Traço fino com um halo leve do próprio verde.
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1.6, rx * 0.04);
    ctx.shadowColor = 'rgba(34,176,125,0.35)';
    ctx.shadowBlur = Math.max(2, rx * 0.04);
    ctx.strokeStyle = 'rgba(34,176,125,0.3)';
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, Math.PI, 0);
    ctx.stroke();
    var g = ctx.createLinearGradient(cx - rx, 0, cx + rx, 0);
    g.addColorStop(0, 'rgba(34,176,125,0.3)');
    g.addColorStop(0.16, 'rgba(34,176,125,1)');
    g.addColorStop(0.84, 'rgba(34,176,125,1)');
    g.addColorStop(1, 'rgba(34,176,125,0.3)');
    ctx.strokeStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI);
    ctx.stroke();
    ctx.restore();
  }

  window.VSBoneca = {
    indice: indice,

    disponivel: function () {
      var m = window.VSBonecaUrls;
      return !!(m && m.busto && m.busto.length === 7);
    },

    /**
     * Proporção largura/altura do que vai ser pintado. Quem monta o canvas usa
     * isto no `aspect-ratio`: só o JS sabe qual faixa do quadro o recorte usa, e
     * com a proporção certa não sobra letterbox para o corpo nadar dentro.
     */
    proporcao: function (recorte) {
      var f = FAIXAS[recorte] || FAIXAS.cheio;
      return LARG / ((f[1] - f[0]) * ALT);
    },

    /**
     * Pinta o manequim. Assíncrono: as três camadas são imagens. Enquanto elas
     * não chegam o canvas fica vazio — quem chama já mostra a foto do produto
     * ao lado, então não há buraco visível.
     */
    desenhar: function (canvas, corpo, opcoes) {
      opcoes = opcoes || {};
      if (!this.disponivel()) return Promise.reject(new Error('sem imagens'));

      // `opcoes.indices` fixa a variação por região (0..6). É o que o passo de
      // ajuste usa: o slider anda 2 cm por passo e uma variação vale 7–8 cm,
      // então pelo centímetro o corpo quase nunca mudava na tela.
      var fixo = opcoes.indices || {};
      // `!= null`, não `||`: a variação 0 existe e é falsy.
      var iBusto = fixo.busto != null ? fixo.busto : indice(corpo.busto, 'busto');
      var iCintura = fixo.cintura != null ? fixo.cintura : indice(corpo.cintura, 'cintura');
      var iQuadril = fixo.quadril != null ? fixo.quadril : indice(corpo.quadril, 'quadril');

      // Guarda de ordem, por canvas: arrastando o slider cada ajuste dispara um
      // carregamento, e o do corpo ANTERIOR pode resolver depois do atual —
      // pintando um corpo que a cliente já passou. O último pedido vence.
      var pedido = (canvas.__vsPedido || 0) + 1;
      canvas.__vsPedido = pedido;

      return Promise.all([
        carregar('busto', iBusto),
        carregar('cintura', iCintura),
        carregar('quadril', iQuadril)
      ]).then(function (imgs) {
        if (canvas.__vsPedido !== pedido) return false;

        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var W = Math.max(1, Math.round(canvas.clientWidth * dpr));
        var H = Math.max(1, Math.round(canvas.clientHeight * dpr));
        if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }

        var ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, W, H);

        var q = metricas(W, H, opcoes.recorte);

        imgs = folhasCosturadas(imgs, iBusto + '-' + iCintura + '-' + iQuadril);

        // quadril é a base; cintura e busto entram por cima, cada um sumindo
        // antes do marco da folha de baixo. Só a cintura leva o corredor — é a
        // troca em que o braço das duas folhas discorda.
        camada(ctx, imgs[2], W, H, q, {});
        camada(ctx, imgs[1], W, H, q, { troca: TROCA.cintura, corredor: true, brilho: imgs.brilho || BRILHO_CINTURA, braco: imgs.braco });
        camada(ctx, imgs[0], W, H, q, { troca: TROCA.busto });

        var fonte = { busto: ['busto', iBusto, 'b'], cintura: ['cintura', iCintura, 'c'], quadril: ['quadril', iQuadril, 'q'] };
        (opcoes.destaques || []).forEach(function (nome) {
          var f = fonte[nome];
          if (!f) return;
          var ext = (silhueta(f[0], f[1]) || {})[f[2]];
          if (!ext) return;
          fita(ctx, q, ext, ALTURAS[nome] * ALT);
        });

        return true;
      });
    }
  };
})();
